import Reveal from "./Reveal";
import MarqueeLink from "./MarqueeLink";
import SEO from "./SEO";
import ThreeImageSlider from "./ThreeImageSlider";
import { IMAGES } from "../lib/assets";

// Default placeholder gallery — reused across every service sub-page until
// Miguel supplies per-service imagery via the `gallery` prop.
// [INSERT GALLERY — 6 project images per service, keyword-rich alt text]
const DEFAULT_GALLERY = [
  { src: IMAGES.flooringDetail, alt: "Placeholder — replace with service project image" },
  { src: IMAGES.interiorFinishing, alt: "Placeholder — replace with service project image" },
  { src: IMAGES.plastering, alt: "Placeholder — replace with service project image" },
  { src: IMAGES.tiling, alt: "Placeholder — replace with service project image" },
  { src: IMAGES.minimalLiving, alt: "Placeholder — replace with service project image" },
  { src: IMAGES.residentialWarm, alt: "Placeholder — replace with service project image" },
];

/**
 * Shared layout for every /services/<slug> sub-page. Keeps copy, images and
 * SEO tags per-page while enforcing a single consistent structure.
 *
 * Props:
 *   eyebrow       – short label above the H1 (e.g. "Services — Flooring")
 *   h1            – page title (H1)
 *   heroImage     – hero background image URL
 *   heroAlt       – alt text for hero image (keyword-rich)
 *   intro         – array of paragraph strings (rendered in order)
 *   offerings     – { heading: "...", items: ["...", "..."] }
 *   why           – { heading: "...", paragraph: "...", points: ["..."] }
 *   seo           – { title, description, canonical }
 */
export default function ServicePageTemplate({
  eyebrow,
  h1,
  heroImage,
  heroAlt,
  intro = [],
  offerings,
  why,
  seo,
  gallery,
}) {
  return (
    <div
      data-testid={`service-subpage-${(seo && seo.slug) || "page"}`}
      className="bg-[#F5F5F0]"
    >
      <SEO
        title={seo?.title}
        description={seo?.description}
        canonical={seo?.canonical}
      />

      {/* HERO */}
      <section className="relative overflow-hidden -mt-32 md:-mt-36">
        <img
          src={heroImage}
          alt={heroAlt}
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black/75" />
        <div className="relative max-w-[1440px] mx-auto px-6 md:px-10 lg:px-16 pt-44 md:pt-56 pb-32 md:pb-44">
          <Reveal>
            <span className="eyebrow text-[#EB1D25]">{eyebrow}</span>
          </Reveal>
          <Reveal delay={120}>
            <h1 className="mt-10 font-heading text-3xl md:text-5xl lg:text-6xl font-medium text-white tracking-tight max-w-[22ch] leading-[1.1]">
              {h1}
            </h1>
          </Reveal>
        </div>
      </section>

      {/* INTRO */}
      <section className="py-32 md:py-48 border-t border-[#E2E2DF]">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 lg:px-16 grid grid-cols-1 md:grid-cols-12 gap-16">
          <div className="md:col-span-4">
            <Reveal>
              <span className="eyebrow-muted">01 — Overview</span>
            </Reveal>
          </div>
          <div className="md:col-span-8">
            {intro.map((p, i) => (
              <Reveal key={i} delay={i * 80}>
                <p
                  className={`text-[#111111] text-base md:text-lg leading-relaxed max-w-[60ch] ${
                    i > 0 ? "mt-8" : ""
                  }`}
                >
                  {p}
                </p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* GALLERY SLIDER — 3 images per page */}
      <ThreeImageSlider
        images={gallery && gallery.length > 0 ? gallery : DEFAULT_GALLERY}
        testid={`service-${(seo && seo.slug) || "page"}-gallery`}
      />

      {/* OFFERINGS */}
      <section className="py-32 md:py-48 border-t border-[#E2E2DF]">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 lg:px-16 grid grid-cols-1 md:grid-cols-12 gap-16">
          <div className="md:col-span-4">
            <Reveal>
              <span className="eyebrow-muted">02 — What we offer</span>
              <h2 className="mt-8 font-heading text-2xl md:text-3xl text-[#111111] leading-[1.2] font-medium tracking-tight">
                {offerings.heading}
              </h2>
            </Reveal>
          </div>
          <div className="md:col-span-8">
            <ul>
              {offerings.items.map((item, i) => (
                <Reveal key={i} delay={i * 60} as="li">
                  <div className="flex items-baseline gap-8 py-6 border-t border-[#E2E2DF]">
                    <span className="font-heading text-xs tracking-[0.25em] text-[#EB1D25] font-semibold w-8 shrink-0">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <h3
                      className="text-[#111111] text-base md:text-lg font-heading font-normal"
                      data-testid={`service-offering-${i}`}
                    >
                      {item}
                    </h3>
                  </div>
                </Reveal>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* WHY TRUST FLOORING */}
      <section className="py-32 md:py-48 border-t border-[#E2E2DF]">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 lg:px-16 grid grid-cols-1 md:grid-cols-12 gap-16">
          <div className="md:col-span-4">
            <Reveal>
              <span className="eyebrow-muted">03 — Why us</span>
              <h2 className="mt-8 font-heading text-2xl md:text-3xl text-[#111111] leading-[1.2] font-medium tracking-tight">
                {why.heading}
              </h2>
            </Reveal>
          </div>
          <div className="md:col-span-8">
            <Reveal delay={100}>
              <p className="text-[#111111] text-base md:text-lg leading-relaxed max-w-[60ch]">
                {why.paragraph}
              </p>
            </Reveal>
            {why.points && why.points.length > 0 && (
              <ul className="mt-10">
                {why.points.map((p, i) => (
                  <Reveal key={i} delay={160 + i * 60} as="li">
                    <div className="flex items-baseline gap-8 py-5 border-t border-[#E2E2DF]">
                      <span className="font-heading text-xs tracking-[0.25em] text-[#EB1D25] font-semibold w-8 shrink-0">
                        {String(i + 1).padStart(2, "0")}
                      </span>
                      <p className="text-[#111111] text-sm md:text-base font-heading font-normal">
                        {p}
                      </p>
                    </div>
                  </Reveal>
                ))}
              </ul>
            )}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-32 md:py-48 border-t border-[#E2E2DF]">
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 lg:px-16">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-12 items-end">
            <div className="md:col-span-8">
              <Reveal>
                <span className="eyebrow">Get in Touch</span>
                <h2 className="mt-8 font-heading text-2xl md:text-4xl text-[#111111] leading-[1.2] font-medium tracking-tight max-w-2xl">
                  Working on a project? Get in touch.
                </h2>
              </Reveal>
            </div>
            <div className="md:col-span-4 md:text-right">
              <Reveal delay={120}>
                <MarqueeLink
                  to="/contact"
                  variant="ghost-dark"
                  data-testid="service-subpage-cta-contact"
                >
                  Get in touch
                </MarqueeLink>
              </Reveal>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
